/**
 * TeknikServisInfoModal
 *
 * Salt-okunur teknik servis bilgi modali. Servis Kaydı Oluştur akışında
 * "Teknik Servis Seç" adımındaki arama satırlarındaki info butonu ile açılır.
 * Yanlış teknik servis seçilmesini önlemek için tıklayan kullanıcıya servis
 * detaylarını (ad, kod, iletişim, il/ilçe, adres, açıklama) gösterir.
 */
import React, { useEffect, useState } from 'react';
import { fetchTechServiceDetail } from '../services/techServicesService';
import { sanitizeString } from '../utils/security';

const safe = (v) => sanitizeString(v ?? '');

const TeknikServisInfoModal = ({ teknikServisId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!teknikServisId) return;
      setLoading(true);
      setError('');
      try {
        const res = await fetchTechServiceDetail(teknikServisId);
        if (cancelled) return;
        const ts = res?.veri?.teknikServis || res?.veri || res;
        setData(ts || null);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Teknik servis bilgileri yüklenemedi');
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [teknikServisId]);

  if (!teknikServisId) return null;

  const ilAd = data?.il?.name || data?.il?.ad || '';
  const ilceAd = data?.ilce?.name || data?.ilce?.ad || '';
  const konum = (data?.konum && typeof data.konum === 'object')
    ? data.konum.adres || data.konum.ad || ''
    : (typeof data?.konum === 'string' ? data.konum : '');

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ flex: '0 0 160px', fontSize: '13px', fontWeight: 600, color: '#475569' }}>{label}</div>
      <div style={{ flex: 1, fontSize: '14px', color: '#1f2937', wordBreak: 'break-word' }}>{value || '—'}</div>
    </div>
  );

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '720px', width: '95%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <h2>
            <i className="fas fa-info-circle" style={{ marginRight: 8, color: '#2563eb' }}></i>
            Teknik Servis Bilgileri
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginRight: 8 }}></i>
              Yükleniyor...
            </div>
          ) : error ? (
            <div style={{ padding: '20px', color: '#b91c1c', background: '#fee2e2', borderRadius: '8px' }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
              {safe(error)}
            </div>
          ) : !data ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Bilgi bulunamadı</div>
          ) : (
            <div>
              <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: data.aktif === false ? '#fee2e2' : '#dcfce7',
                    color: data.aktif === false ? '#b91c1c' : '#166534',
                  }}
                >
                  {data.aktif === false ? 'Pasif' : 'Aktif'}
                </span>
              </div>
              <Row label="Ad" value={safe(data.ad || data.name)} />
              <Row label="Kod" value={safe(data.kod)} />
              <Row label="Telefon" value={safe(data.telefon)} />
              <Row label="E-posta" value={safe(data.mail || data.email)} />
              <Row label="Yetkili Kişi" value={safe(data.yetkiliKisi || data.yetkili)} />
              <Row label="Yetkili Telefon" value={safe(data.yetkiliTelefon)} />
              <Row label="Faks" value={safe(data.faks)} />
              <Row label="İl / İlçe" value={safe([ilceAd, ilAd].filter(Boolean).join(' / '))} />
              <Row label="Adres" value={safe(data.adres || konum)} />
              {data.aciklama && <Row label="Açıklama" value={safe(data.aciklama)} />}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ flexShrink: 0 }}>
          <button type="button" className="secondary-btn" onClick={onClose}>
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeknikServisInfoModal;
